import { useEffect, useState } from "react";
import { X, Truck, Loader2, AlertCircle } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { validateBookingForOut, CARRIERS_REQUIRING_BOOKING_ON_OUT } from "../lib/ediRequirements";
import { downloadGateWaybillPdf } from "../lib/exporters";

// Format a JS Date into the local-datetime-input format ("YYYY-MM-DDTHH:mm").
// datetime-local inputs don't accept full ISO strings with seconds/timezone.
const toLocalInputValue = (d) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Session-memory key — persists the last-used consignee / contact_number / booking so
// the next gate-out dialog on the same session pre-fills them (operator can
// still edit).  Cleared when the browser tab closes.
const GATE_OUT_MEMORY_KEY = "gateOut.lastUsed";
const readMemory = () => {
  try { return JSON.parse(sessionStorage.getItem(GATE_OUT_MEMORY_KEY) || "{}"); }
  catch { return {}; }
};
const writeMemory = (obj) => {
  try { sessionStorage.setItem(GATE_OUT_MEMORY_KEY, JSON.stringify(obj)); }
  catch { /* private mode / storage disabled — fine to silently skip */ }
};

/**
 * GateOutDialog — slide-over dialog to record a gate-OUT for an existing in-depot container.
 * Required fields per business rule: consignee (customer), truck_plate, driver_name.
 * Contact number is optional (replaced the redundant "Agent" free-text 2026-07-30).
 */
export default function GateOutDialog({ container, onClose, onDone }) {
  const remembered = readMemory();
  const [form, setForm] = useState({
    customer: remembered.customer || "",
    contact_number: remembered.contact_number || "",
    truck_plate: "",
    driver_name: "",
    driver_license: "",
    booking_number: remembered.booking_number || "",
    vessel_name: remembered.vessel_name || "",
    seal_number: "",
    event_time: toLocalInputValue(new Date()),
    notes: "",
    services_performed: [],
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [recipientHint, setRecipientHint] = useState(null);
  const [releaseOrders, setReleaseOrders] = useState([]);
  const [pickedRoId, setPickedRoId] = useState("");
  const [recentParties, setRecentParties] = useState([]);
  const [partners, setPartners] = useState([]);

  // Load partners once (mount) — feeds canonical legal_names into the
  // Consignee datalist so operators pick the same string every time
  // instead of typing "Stolt Rotterdam" / "Stolt B.V." / etc.
  useEffect(() => {
    api.get("/partners?fields=code,legal_name")
      .then((r) => setPartners(Array.isArray(r.data) ? r.data : []))
      .catch(() => setPartners([]));
  }, []);

  useEffect(() => {
    if (!container) return;
    const mem = readMemory();
    setForm({
      customer: mem.customer || "",
      contact_number: mem.contact_number || "",
      truck_plate: "",
      driver_name: "",
      driver_license: "",
      booking_number: mem.booking_number || "",
      vessel_name: mem.vessel_name || "",
      seal_number: "",
      event_time: toLocalInputValue(new Date()),
      notes: "",
      services_performed: [],
    });
    setErr("");
    const carrier = (container.last_carrier || container.carrier || container.carrier_code || container.iso_carrier || "").toUpperCase();
    if (carrier) {
      api.get(`/edi-recipients/by-carrier/${carrier}`)
        .then((r) => setRecipientHint(r.data))
        .catch(() => setRecipientHint(null));
      // Pull matching OPEN release orders so operators can pick one
      // instead of typing the booking by hand.
      const iso = container.iso_type || "22G1";
      api.get(`/release-orders/match/for-container?carrier_code=${carrier}&iso_type=${iso}`)
        .then((r) => setReleaseOrders(r.data || []))
        .catch(() => setReleaseOrders([]));
      // Recent (customer, booking, contact_number) tuples for the datalist autocomplete.
      // Scoped to this carrier so we're not suggesting HLC bookings on an STC
      // gate-out, etc.
      api.get(`/gate-events/recent-parties?carrier_code=${carrier}&days=60&limit=50`)
        .then((r) => setRecentParties(r.data || []))
        .catch(() => setRecentParties([]));
    } else {
      setRecipientHint(null);
      setReleaseOrders([]);
      setRecentParties([]);
    }
    setPickedRoId("");
  }, [container]);

  if (!container) return null;
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // When the operator picks/types a booking_number that matches a known
  // recent-parties tuple, auto-fill the consignee + contact_number to save re-typing.
  const applyBookingAutofill = (bkNo) => {
    const match = recentParties.find(
      (p) => p.booking_number && p.booking_number.toLowerCase() === bkNo.toLowerCase()
    );
    if (!match) return;
    setForm((f) => ({
      ...f,
      customer: f.customer || match.customer,
      contact_number: f.contact_number || match.contact_number,
    }));
  };
  // Same idea in reverse — if consignee matches a recent tuple with a
  // consistent booking, fill in the booking too.
  const applyConsigneeAutofill = (customer) => {
    const match = recentParties.find(
      (p) => p.customer && p.customer.toLowerCase() === customer.toLowerCase() && p.booking_number
    );
    if (!match) return;
    setForm((f) => ({
      ...f,
      booking_number: f.booking_number || match.booking_number,
      contact_number: f.contact_number || match.contact_number,
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (submitting) return;
    setSubmitting(true);
    try {
      const carrier = (container.last_carrier || container.carrier || container.carrier_code || "").toUpperCase() || null;
      // Strict pre-flight: ARKAS (and other configured carriers) reject EDI
      // without a booking / service-order reference. Block the save here so
      // operators can't ship bad data downstream.
      const bookingErr = validateBookingForOut(carrier, form.booking_number);
      if (bookingErr) {
        setErr(bookingErr);
        setSubmitting(false);
        return;
      }
      const payload = {
        container_number: container.container_number,
        container_iso_type: container.iso_type || "22G1",
        gate_type: "OUT",
        is_full: false,
        // Operator-entered gate time (datetime-local -> ISO). Falls back to
        // "now" if the field somehow ends up blank so we never send a null.
        event_time: form.event_time
          ? new Date(form.event_time).toISOString()
          : new Date().toISOString(),
        location_code: container.last_location_code || "GHTEM",
    location_name: "Multi Purpose Terminal Ghana (MTG)",
        yard_code: container.yard_code || "TEMA",
        last_carrier: carrier,
        carrier_code: carrier,
        customer: form.customer.trim(),
        contact_number: form.contact_number.trim() || null,
        truck_plate: form.truck_plate.toUpperCase().trim(),
        driver_name: form.driver_name.trim(),
        driver_license: form.driver_license.trim() || null,
        chassis_number: null,
        seal_number: form.seal_number || null,
        booking_number: form.booking_number || null,
        bill_of_lading: null,
        vessel_name: form.vessel_name.trim() || null,
        release_order_id: pickedRoId || null,
        notes: form.notes || null,
        services_performed: form.services_performed,
        grade: "B",
        condition: "SOUND",
        repair_status: "NONE",
        idempotency_key: crypto.randomUUID(),
      };
      const { data } = await api.post("/gate-events", payload);
      // Remember booking / consignee / contact_number for the next gate-out in this
      // session so the operator doesn't retype the same "consignee: Sunda +
      // booking: BKG12345" combo for 20 tanks in a row.
      writeMemory({
        customer: form.customer.trim(),
        contact_number: form.contact_number.trim(),
        booking_number: form.booking_number || "",
        vessel_name: form.vessel_name.trim(),
      });
      const auto = data._auto_edi;
      if (auto?.status === "SENT") {
        toast.success(`${container.container_number} gated OUT · EDI sent`);
      } else if (auto?.status === "FAILED") {
        toast.error(`Gated OUT but EDI failed: ${auto.last_error?.slice(0, 80) || "see EDI page"}`);
      } else if (recipientHint?.found) {
        toast.success(`${container.container_number} gated OUT · EDI queued`);
      } else {
        toast.success(`${container.container_number} gated OUT (no EDI mapping for carrier)`);
      }
      onDone?.(data);
      onClose();

      // Print the driver's paper trail. Fire-and-forget so a rendering
      // hiccup never blocks a successful gate-OUT — the same PDF is
      // always retrievable from the Waybill button on Gate Activity.
      downloadGateWaybillPdf(data).catch((err) => {
        console.warn("waybill download failed:", err);
        toast.warning("Gate OUT saved, but the waybill PDF could not be generated. Use the Waybill button on Gate Activity to reprint.");
      });
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-stretch justify-end" data-testid="gate-out-dialog">
      <div className="w-full max-w-xl bg-white h-full overflow-y-auto border-l border-gray-200">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-sm bg-amber-50 text-amber-700 flex items-center justify-center">
              <Truck className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Gate OUT</div>
              <div className="font-mono text-lg font-bold">{container.container_number}</div>
              <div className="text-xs text-gray-500 font-mono">
                {container.iso_type} · last carrier {container.last_carrier || container.carrier || container.carrier_code || "—"}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="ps-btn ps-btn-secondary" data-testid="close-gate-out">
            <X className="w-4 h-4" /> Close
          </button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-5">
          {recipientHint?.found && recipientHint.auto_send && (
            <div className="border border-blue-200 bg-blue-50 px-3 py-2 rounded-sm text-xs text-blue-900" data-testid="auto-edi-hint">
              ⚡ Auto-EDI is configured for carrier <strong className="font-mono">{recipientHint.carrier_code}</strong> → <span className="font-mono">{recipientHint.email}</span>. CODECO95B will be emailed automatically on submit.
            </div>
          )}
          {recipientHint && !recipientHint.found && (
            <div className="border border-amber-200 bg-amber-50 px-3 py-2 rounded-sm text-xs text-amber-900">
              ⚠️ No EDI recipient mapping for this carrier. The gate-OUT will be recorded but no EDI will be sent automatically. Add a mapping in Settings → EDI Recipients.
            </div>
          )}

          {err && (
            <div className="flex items-start gap-2 border border-red-200 bg-red-50 px-3 py-2 rounded-sm text-sm text-red-700" data-testid="gate-out-error">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{err}</span>
            </div>
          )}

          <section>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500 mb-3">Pickup details (optional)</div>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">
                  Gate-out time
                  <span className="ml-2 text-[10px] font-normal normal-case tracking-normal text-gray-500">defaults to now — backdate if entering after the truck left</span>
                </label>
                <input
                  type="datetime-local"
                  value={form.event_time}
                  onChange={(e) => update("event_time", e.target.value)}
                  className="ps-input"
                  data-testid="gate-out-event-time"
                  required
                />
              </div>
              <div className="col-span-2">
                <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Consignee</label>
                <input value={form.customer} onChange={(e) => update("customer", e.target.value)}
                  onBlur={(e) => applyConsigneeAutofill(e.target.value.trim())}
                  list="gate-out-consignee-suggestions"
                  placeholder="e.g. Sunda Ghana Ltd" className="ps-input" data-testid="gate-out-consignee" />
                <datalist id="gate-out-consignee-suggestions">
                  {(() => {
                    // Merge canonical partner legal_names (top-priority) with
                    // recent-parties history, deduped. Canonical names sit
                    // above history entries so the datalist visually surfaces
                    // the "correct" spelling first.
                    const canonical = partners.map((p) => p.legal_name).filter(Boolean);
                    const historic = recentParties.filter((p) => p.customer).map((p) => p.customer);
                    const seen = new Set();
                    const out = [];
                    for (const s of [...canonical, ...historic]) {
                      const key = s.trim().toUpperCase();
                      if (!key || seen.has(key)) continue;
                      seen.add(key);
                      out.push(s);
                    }
                    return out.map((c) => <option key={c} value={c} />);
                  })()}
                </datalist>
              </div>
              <div className="col-span-2">
                <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Contact number</label>
                <input value={form.contact_number} onChange={(e) => update("contact_number", e.target.value)}
                  list="gate-out-contact-suggestions"
                  placeholder="e.g. +233 24 123 4567 (optional)" className="ps-input font-mono" data-testid="gate-out-contact-number" />
                <datalist id="gate-out-contact-suggestions">
                  {[...new Set(recentParties.filter((p) => p.contact_number).map((p) => p.contact_number))].map((a) => (
                    <option key={a} value={a} />
                  ))}
                </datalist>
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Truck plate</label>
                <input value={form.truck_plate} onChange={(e) => update("truck_plate", e.target.value.toUpperCase())}
                  placeholder="GR-2244-22" className="ps-input font-mono" data-testid="gate-out-truck" />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Driver name</label>
                <input value={form.driver_name} onChange={(e) => update("driver_name", e.target.value)}
                  placeholder="Full name" className="ps-input" data-testid="gate-out-driver" />
              </div>
              <div className="col-span-2">
                <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Driver license number</label>
                <input value={form.driver_license} onChange={(e) => update("driver_license", e.target.value.toUpperCase())}
                  placeholder="Certification / license # (optional)" className="ps-input font-mono" data-testid="gate-out-driver-license" maxLength={40} />
              </div>
            </div>
          </section>

          <section>
            {(() => {
              const cc = (container.last_carrier || container.carrier || container.carrier_code || "").toUpperCase();
              const bookingRequired = CARRIERS_REQUIRING_BOOKING_ON_OUT.has(cc);
              // Build a flat picker list: one option per (release_order, booking)
              // so operators can pick the exact destination/booking they want
              // this container to consume a slot for.
              const pickOptions = [];
              for (const ro of releaseOrders) {
                for (const bk of ro.bookings) {
                  for (const ln of bk.lines) {
                    pickOptions.push({
                      key: `${ro.id}|${bk.booking_no}|${ln.iso_type}`,
                      ro_id: ro.id,
                      booking_no: bk.booking_no,
                      label: `${bk.booking_no} — ${bk.destination || "—"} · ${ln.remaining}/${ln.qty} of ${ln.iso_type} remaining (TO ${ro.transport_order || "—"})`,
                    });
                  }
                }
              }
              const onPick = (val) => {
                setPickedRoId("");
                update("booking_number", "");
                if (!val) return;
                const opt = pickOptions.find((o) => o.key === val);
                if (opt) {
                  setPickedRoId(opt.ro_id);
                  update("booking_number", opt.booking_no);
                }
              };
              return (
                <>
                  {pickOptions.length > 0 && (
                    <div className="mb-4 border border-[#006E9E] bg-[#EBF5FB]/60 p-3">
                      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#006E9E] mb-2">Pick from open release orders</div>
                      <select
                        className="ps-input font-mono w-full"
                        value={pickedRoId ? pickOptions.find((o) => o.ro_id === pickedRoId && o.booking_no === form.booking_number)?.key || "" : ""}
                        onChange={(e) => onPick(e.target.value)}
                        data-testid="gate-out-release-order-picker"
                      >
                        <option value="">— Select a release order to auto-fill the booking —</option>
                        {pickOptions.map((o) => (
                          <option key={o.key} value={o.key}>{o.label}</option>
                        ))}
                      </select>
                      <div className="text-[11px] text-gray-500 mt-1">
                        Picking here will stamp the gate-OUT with this booking and decrement the slot automatically. You can still type the booking by hand below if it's not in this list.
                      </div>
                    </div>
                  )}
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500 mb-3">
                    {bookingRequired ? `${cc} references (booking required for EDI)` : "Optional references"}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-[10px] font-bold uppercase tracking-[0.16em] mb-1 ${bookingRequired ? "text-[#F1471D]" : "text-gray-600"}`}>
                        Booking / Service-Order #{bookingRequired && " *"}
                      </label>
                      <input value={form.booking_number} onChange={(e) => update("booking_number", e.target.value)}
                        onBlur={(e) => applyBookingAutofill(e.target.value.trim())}
                        list="gate-out-booking-suggestions"
                        placeholder={bookingRequired ? `Required by ${cc} for gate-OUT EDI` : "e.g. HLCUBKG12345"}
                        className={`ps-input font-mono ${bookingRequired && !form.booking_number ? "border-[#F1471D] bg-[#FFF1ED]" : ""}`}
                        data-testid="gate-out-booking"
                        required={bookingRequired}
                      />
                      <datalist id="gate-out-booking-suggestions">
                        {[...new Set(recentParties.filter((p) => p.booking_number).map((p) => p.booking_number))].map((b) => (
                          <option key={b} value={b} />
                        ))}
                      </datalist>
                      {bookingRequired && !form.booking_number && (
                        <div className="text-[11px] text-[#F1471D] mt-1" data-testid="booking-warning">
                          {cc} rejects gate-OUT EDI without this — paperwork should show the booking or release-order number.
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Seal number</label>
                      <input value={form.seal_number} onChange={(e) => update("seal_number", e.target.value)} className="ps-input font-mono" data-testid="gate-out-seal" />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Vessel</label>
                      <input value={form.vessel_name} onChange={(e) => update("vessel_name", e.target.value)}
                        placeholder="e.g. MSC IRINA (optional — required by GPHA on waybill)"
                        className="ps-input" maxLength={80} data-testid="gate-out-vessel" />
                    </div>
                  </div>
                </>
              );
            })()}
          </section>
          <section>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500 mb-3">Notes</div>
            <textarea value={form.notes} onChange={(e) => update("notes", e.target.value)} rows={2} className="ps-input w-full" data-testid="gate-out-notes" />
          </section>

          <section>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500 mb-3">Billable services performed (fallback)</div>
            <div className="text-[11px] bg-amber-50 border border-amber-200 text-amber-900 px-3 py-2 rounded-sm mb-3">
              Services should be logged from the <b>Container Detail → Yard services</b> panel <b>while the container is in the yard</b>, not here.
              Only tick a box below if the service was performed but never logged in the yard.
            </div>
            <div className="grid grid-cols-2 gap-2">
              {[
                { code: "INSPECTION", label: "Inspection Fee" },
                { code: "WASHING", label: "Washing" },
                { code: "SPECIAL_CLEANING", label: "Special Cleaning" },
                { code: "SHORT_PTI", label: "Short PTI (Visual)" },
                { code: "EXTENDED_PTI", label: "Extended PTI" },
                { code: "FOOD_UPGRADE", label: "Food Upgrade" },
              ].map((s) => {
                const checked = form.services_performed.includes(s.code);
                return (
                  <label key={s.code} className={`flex items-center gap-2 px-3 py-2 border rounded-sm text-sm cursor-pointer transition-colors ${checked ? "border-[#006E9E] bg-blue-50" : "border-gray-200 hover:bg-gray-50"}`} data-testid={`service-${s.code}`}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? [...form.services_performed, s.code]
                          : form.services_performed.filter((c) => c !== s.code);
                        update("services_performed", next);
                      }}
                    />
                    <span>{s.label}</span>
                  </label>
                );
              })}
            </div>
            <div className="text-[11px] text-gray-500 mt-2">Handling and storage (if past free days) are billed automatically per the carrier's tariff. Check additional services performed before gate-out.</div>
          </section>

          <div className="sticky bottom-0 bg-white border-t border-gray-200 -mx-6 px-6 py-4 flex justify-end gap-2">
            <button type="button" onClick={onClose} className="ps-btn ps-btn-secondary">Cancel</button>
            <button type="submit" disabled={submitting} className="ps-btn ps-btn-primary" data-testid="submit-gate-out">
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Gating out…</> : <><Truck className="w-4 h-4" /> Confirm & print waybill</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
